import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../screens/firebaseConfig';
import { apiUrl } from '../screens/utils/api';

/**
 * Resolves the logged-in Firebase user's application role (and company) from the backend
 * (`GET /api/users/me?email=`) and exposes it to the menu + route guards.
 * A signed-in user with no account yet comes back with `needsOnboarding` and is sent through
 * self-onboarding instead of being told to wait for an administrator. `refresh()` re-reads the role
 * once onboarding has created the account.
 */
const RoleContext = createContext({ role: null, user: null, loadingRole: true, needsOnboarding: false, refresh: () => {} });

export function RoleProvider({ children }) {
  const [fbUser, authLoading] = useAuthState(auth);
  const [role, setRole] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [loadingRole, setLoadingRole] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    async function resolveRole() {
      if (authLoading) return;
      if (!fbUser) {
        delete axios.defaults.headers.common['X-Tenant-User'];
        setRole(null);
        setAppUser(null);
        setLoadingRole(false);
        return;
      }
      // The verified Firebase ID token is now the real identity (see auth/attachIdToken.js); the
      // backend prefers it and only consults this header when no token arrived. It is still sent
      // because the API runs in `permissive` mode during the transition and the two sides deploy
      // separately — REMOVE THIS LINE once viai.auth.mode=required, at which point the header is
      // ignored server-side anyway.
      axios.defaults.headers.common['X-Tenant-User'] = fbUser.email;
      setLoadingRole(true);
      try {
        const res = await axios.get(`${apiUrl}/api/users/me`, { params: { email: fbUser.email } });
        if (!cancelled) {
          // A user with no configured role resolves to null → onboarding (new customer) or the
          // "role needs configuring" screen (invited but not yet set up).
          setRole(res.data?.role || null);
          setAppUser(res.data || null);
        }
      } catch (err) {
        // Couldn't resolve the role (e.g. backend blip) — treat as unconfigured rather than granting
        // full access; the UI shows the "role needs configuring" screen with a way to retry/sign out.
        if (!cancelled) {
          setRole(null);
          setAppUser(null);
        }
      } finally {
        if (!cancelled) setLoadingRole(false);
      }
    }
    resolveRole();
    return () => { cancelled = true; };
  }, [fbUser, authLoading, reloadKey]);

  return (
    <RoleContext.Provider value={{
      role,
      user: appUser,
      loadingRole: authLoading || loadingRole,
      needsOnboarding: !!appUser?.needsOnboarding,
      refresh,
    }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  return useContext(RoleContext);
}

export default RoleContext;
